export const apiUrl = process.env.NEXT_PUBLIC_API_URL;
export const wordpressApiUrl = process.env.NEXT_PUBLIC_WORDPRESS_API_URL;

type QueryParam = number | string;

export const apiRoutes = {
  share: {
    _root: `${apiUrl}/share/`,
    container: (containerId: QueryParam) => ({
      _root: `${apiUrl}/share/${containerId}/`,
      candidate: (candidateId: QueryParam) => ({
        _root: `${apiUrl}/share/${containerId}/candidates/${candidateId}/`,
        pdf: (pdfId: QueryParam) =>
          `${apiUrl}/share/${containerId}/candidates/${candidateId}/pdf/${pdfId}/`,
      }),
    }),
  },
  candidates: {
    _root: `${apiUrl}/application/`,
    applyAndUpdateProfile: `${apiUrl}/application/apply-and-update-profile/`,
    createManually: `${apiUrl}/application/create-manually/`,
  },
  offers: {
    _root: `${apiUrl}/offer/`,
    offer: (offerId: QueryParam) => `${apiUrl}/offer/${offerId}/`,
    recommended: `${apiUrl}/offer/recommended/`,
  },
  employer: {
    candidates: `${apiUrl}/employer/candidates/`,
  },
  crmrequest: `${apiUrl}/crmrequest/`,
  browser: {
    applications: {
      candidate: (candidateId: QueryParam) =>
        `${apiUrl}/browser/applications/${candidateId}/`,
    },
    activityRequest: (id: QueryParam) =>
      `${apiUrl}/browser/applications/${id}/send-status-request/`,
    candidates: {
      _root: `${apiUrl}/browser/applications/`,
      candidate: (candidateId: QueryParam) =>
        `${apiUrl}/browser/applications/${candidateId}/`,
    },
    offerParser: `${apiUrl}/browser/ai-offer-parser/`,
    collections: {
      _root: `${apiUrl}/browser/collections/`,
      collection: (id: QueryParam) => ({
        _root: `${apiUrl}/browser/collections/${id}/`,
        collaborators: `${apiUrl}/browser/collections/${id}/collaborators/`,
        unsubscribe: `${apiUrl}/browser/collections/${id}/unsubscribe/`,
      }),
      possibleCollaborators: `${apiUrl}/browser/collections/possible-collaborators/`,
    },
    chart: `${apiUrl}/browser/chart/`,
    talentPools: `${apiUrl}/browser/talent-pools/`,
  },

  cvGenerator: {
    context: {
      _root: `${apiUrl}/cv-generator/context/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/cv-generator/context/${id}/`,
        translate: `${apiUrl}/cv-generator/context/${id}/translate/`,
        download: `${apiUrl}/cv-generator/context/${id}/download/`,
        adjustToOffer: `${apiUrl}/cv-generator/context/${id}/adjust-to-offer/`,
        groupSkills: `${apiUrl}/cv-generator/context/${id}/group-skills/`,
      }),
      fromFile: `${apiUrl}/cv-generator/context/from-file/`,
      fromCandidateId: (id: QueryParam) =>
        `${apiUrl}/cv-generator/context/from-candidate-id/${id}/`,
      retry: (id: QueryParam) => `${apiUrl}/cv-generator/context/${id}/retry/`,
      downloadZip: `${apiUrl}/cv-generator/context/download-zip/`,
    },
    cez: {
      _root: `${apiUrl}/cv-generator/cez/`,
      fromCandidateId: {
        detail: (id: QueryParam) =>
          `${apiUrl}/cv-generator/cez/from-candidate-id/${id}/`,
      },
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/cv-generator/cez/${id}/`,
        download: `${apiUrl}/cv-generator/cez/${id}/download/`,
        retry: `${apiUrl}/cv-generator/cez/${id}/retry/`,
      }),
    },
    compareWithOffer: (id: QueryParam) =>
      `${apiUrl}/cv-generator/compare-with-offer/${id}/`,
    render: `${apiUrl}/cv-generator/render/`,
    feedback: `${apiUrl}/cv-generator/feedback/`,
    userUsageData: `${apiUrl}/cv-generator/user-usage-data/`,
  },
  common: {
    languages: {
      _root: `${apiUrl}/common/languages/`,
      language: (languageId: QueryParam) =>
        `${apiUrl}/common/languages/${languageId}/`,
    },
    skills: {
      _root: `${apiUrl}/common/skills/`,
      skill: (skillId: QueryParam) => `${apiUrl}/common/skills/${skillId}/`,
    },
    countries: {
      _root: `${apiUrl}/common/countries/`,
      countries: (countriesId: QueryParam) =>
        `${apiUrl}/common/countries/${countriesId}/`,
    },
    cities: {
      _root: `${apiUrl}/common/cities/`,
      cities: (citiesId: QueryParam) => `${apiUrl}/common/cities/${citiesId}/`,
    },
    suggestions: {
      cities: `${apiUrl}/common/suggestions/cities/`,
      countries: `${apiUrl}/common/suggestions/countries/`,
    },
    waitingList: `${apiUrl}/common/waiting-list/`,
    contactUs: `${apiUrl}/common/contact-us/`,
    getDemo: `${apiUrl}/common/get-demo/`,
    helpUsImprove: `${apiUrl}/common/help-us-improve/`,
    recruiterContacts: `${apiUrl}/common/recruiter-contacts/`,
    feedback: `${apiUrl}/common/feedback/`,
  },
  resume: {
    fromFile: `${apiUrl}/resume/from-file/`,
    detail: (id: QueryParam) => `${apiUrl}/resume/${id}/`,
    usage: `${apiUrl}/resume/usage/`,
    buyResumeChecking: `${apiUrl}/resume/buy-resume-checking/`,
    match: {
      _root: `${apiUrl}/resume/match/`,
      detail: (id: QueryParam) => `${apiUrl}/resume/match/${id}/`,
    },
    jobOffer: {
      _root: `${apiUrl}/resume/job_offer/`,
      detail: (id: QueryParam) => `${apiUrl}/resume/job_offer/${id}/`,
    },
    offerRecommendations: {
      _root: `${apiUrl}/resume/offer-recommendations/`,
      tokens: `${apiUrl}/resume/offer-recommendations/tokens/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/resume/offer-recommendations/${id}/`,
        recommendations: `${apiUrl}/resume/offer-recommendations/${id}/recommendations/`,
      }),
    },
  },
  geolocation: `${apiUrl}/geoloc/`,
  profile: {
    me: {
      _root: `${apiUrl}/profile/me/`,
      activeStatus: `${apiUrl}/profile/me/active-status/`,
    },
    deleteRequest: `${apiUrl}/profile/request-account-deletion/`,
    confirmAccountDeletion: `${apiUrl}/profile/confirm-account-deletion/`,
    onboardingAccomplished: `${apiUrl}/profile/onboarding-accomplished/`,
  },
  auth: {
    activate: `${apiUrl}/auth/activate/`,
    activateAndResetPassword: `${apiUrl}/auth/activate-and-reset-password/`,
    activateRequest: `${apiUrl}/auth/activate-request/`,
    register: `${apiUrl}/auth/register/`,
    resetPassword: `${apiUrl}/auth/reset-password/`,
    resetPasswordRequest: `${apiUrl}/auth/reset-password-request/`,
    token: {
      _root: `${apiUrl}/auth/token/`,
      refresh: `${apiUrl}/auth/token/refresh/`,
    },
    logout: `${apiUrl}/auth/logout/`,
    activeCandidateStatusRequest: `${apiUrl}/auth/active-candidate-status-request/`,
    customerRegister: `${apiUrl}/auth/customer-register/`,
    google: {
      _root: `${apiUrl}/auth/google/`,
      oneTap: `${apiUrl}/auth/google/one_tap/`,
    },
    linkedin: {
      callback: `${apiUrl}/auth/linkedin/callback/`,
      loginCandidate: `${apiUrl}/auth/linkedin/login-candidate/`,
      loginCustomer: `${apiUrl}/auth/linkedin/login-customer/`,
    },
    apiKey: {
      _root: `${apiUrl}/auth/api-key/`,
      detail: (id: QueryParam) => `${apiUrl}/auth/api-key/${id}/`,
    },
  },
  applicationWebhooks: {
    _root: `${apiUrl}/application-webhooks/`,
    detail: (id: QueryParam) => ({
      _root: `${apiUrl}/application-webhooks/${id}/`,
      rotateSecret: `${apiUrl}/application-webhooks/${id}/rotate-secret/`,
      test: `${apiUrl}/application-webhooks/${id}/test/`,
    }),
  },
  parseDataFromCv: `${apiUrl}/parse-data-from-cv/`,
  feedback: {
    _root: `${apiUrl}/feedback/`,
    average: `${apiUrl}/feedback/average/`,
  },
  tests: {
    available: {
      _root: `${apiUrl}/tests/available/`,
      test: (id: QueryParam) => `${apiUrl}/tests/available/${id}/`,
      start: (id: QueryParam) => `${apiUrl}/tests/available/${id}/start/`,
    },
    certificates: {
      _root: `${apiUrl}/tests/certificates/`,
      certificate: (id: QueryParam) => `${apiUrl}/tests/certificates/${id}/`,
    },
    started: {
      _root: `${apiUrl}/tests/started/`,
      forRecruiter: (applicationId: QueryParam) =>
        `${apiUrl}/tests/started/for-recruiter/${applicationId}/`,
      cancel: (testUuid: QueryParam) => ({
        _root: `${apiUrl}/tests/started/${testUuid}/cancel/`,
      }),
      test: (testUuid: QueryParam) => ({
        _root: `${apiUrl}/tests/started/${testUuid}/`,
        certificate: `${apiUrl}/tests/started/${testUuid}/certificate/`,
        questions: {
          question: (questionId: QueryParam) => ({
            answer: {
              closed: `${apiUrl}/tests/started/${testUuid}/questions/${questionId}/answer/closed/`,
              open: `${apiUrl}/tests/started/${testUuid}/questions/${questionId}/answer/open/`,
            },
          }),
        },
      }),
    },
  },
  candidateMatching: {
    _root: `${apiUrl}/candidate-matching/`,
    single: (id: QueryParam) => `${apiUrl}/candidate-matching/${id}/`,
  },
  payments: {
    stripe: {
      checkout: (id: QueryParam) => `${apiUrl}/payments/stripe/checkout/${id}/`,
      customerPortal: `${apiUrl}/payments/stripe/customer_portal/`,
      prices: `${apiUrl}/payments/stripe/prices/`,
      packages: `${apiUrl}/payments/stripe/packages/`,
      userCurrentBoughtPrice: `${apiUrl}/payments/stripe/user-current-bought-price/`,
      hasUserUsedTrial: `${apiUrl}/payments/stripe/has-user-used-trial/`,
      pricingPlans: `${apiUrl}/payments/stripe/pricing-plans/`,
      validateAccessCode: `${apiUrl}/payments/stripe/validate-access-code/`,
      redeemCode: `${apiUrl}/payments/stripe/redeem-code/`,
    },
    mindCoins: {
      me: `${apiUrl}/payments/mind-coins/me/`,
      checkout: `${apiUrl}/payments/mind-coins/checkout/125/`,
    },
  },
  jobMatching: {
    offer: {
      _root: `${apiUrl}/job-matching/offer/`,
      generateAiOfferDetails: `${apiUrl}/job-matching/offer/generate-ai-offer-details/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/job-matching/offer/${id}/`,
        candidates: {
          _root: `${apiUrl}/job-matching/offer/${id}/candidates/`,
          candidate: (candidateId: QueryParam) => ({
            _root: `${apiUrl}/job-matching/offer/${id}/candidates/${candidateId}/`,
            downloadPdfSummary: `${apiUrl}/job-matching/offer/${id}/candidates/${candidateId}/download-pdf-summary/`,
            sendSummaryEmail: `${apiUrl}/job-matching/offer/${id}/candidates/${candidateId}/send-summary-email/`,
          }),
        },
        add: {
          fromExternalCandidates: `${apiUrl}/job-matching/offer/${id}/add/from-external-candidates/`,
          fromMindpal: `${apiUrl}/job-matching/offer/${id}/add/from-mindpal/`,
        },
        retry: {
          _root: `${apiUrl}/job-matching/offer/${id}/retry/`,
          candidate: (candidateId: QueryParam) =>
            `${apiUrl}/job-matching/offer/${id}/retry/${candidateId}/`,
        },
        downloadCsv: `${apiUrl}/job-matching/offer/${id}/download-csv/`,
      }),
    },
    cvParser: {
      fromFile: `${apiUrl}/job-matching/cv-parser/from-file/`,
      fromId: (id: QueryParam) =>
        `${apiUrl}/job-matching/cv-parser/from-id/${id}/`,
    },
  },
  externalCandidates: {
    candidates: {
      _root: `${apiUrl}/external-candidates/candidates/`,
      detail: (id: QueryParam) => ({
        root: `${apiUrl}/external-candidates/candidates/${id}/`,
        retry: `${apiUrl}/external-candidates/candidates/${id}/retry/`,
        github: {
          _root: `${apiUrl}/external-candidates/candidates/${id}/github/`,
          repositories: `${apiUrl}/external-candidates/candidates/${id}/github/repositories/`,
        },
      }),
      fromFile: `${apiUrl}/external-candidates/candidates/from-file/`,
      fromRecruiteeId: `${apiUrl}/external-candidates/candidates/from-recruitee-id/`,
    },
    messagePackages: {
      _root: `${apiUrl}/external-candidates/message-packages/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/external-candidates/message-packages/${id}/`,
      }),
    },
    messages: {
      _root: `${apiUrl}/external-candidates/messages/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/external-candidates/messages/${id}/`,
      }),
    },
    csv: {
      _root: `${apiUrl}/external-candidates/csv/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/external-candidates/csv/${id}/`,
        uploadCsv: `${apiUrl}/external-candidates/csv/${id}/upload-csv/`,
        errorsDownload: `${apiUrl}/external-candidates/csv/${id}/errors-download/`,
        errorsData: `${apiUrl}/external-candidates/csv/${id}/errors-data/`,
        progress: `${apiUrl}/external-candidates/csv/${id}/progress/`,
      }),
    },
    collections: {
      _root: `${apiUrl}/external-candidates/collections/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/external-candidates/collections/${id}/`,
        referenceJobMatching: {
          _root: `${apiUrl}/external-candidates/collections/${id}/reference-job-matching/`,
          referenceOfferId: (referenceOfferId: QueryParam) =>
            `${apiUrl}/external-candidates/collections/${id}/reference-job-matching/${referenceOfferId}`,
        },
      }),
    },
    offerCandidatesStages: {
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/external-candidates/offer-candidates-stages/${id}/`,
        candidateNames: `${apiUrl}/external-candidates/offer-candidates-stages/${id}/candidate-names/`,
      }),
    },
  },
  settings: {
    recruiter: {
      mine: `${apiUrl}/settings/recruiter/mine/`,
    },
    templateRequest: {
      _root: `${apiUrl}/settings/template_request/`,
      id: (id: QueryParam) => `${apiUrl}/settings/template_request/${id}/`,
    },
  },
  changelog: {
    _root: `${apiUrl}/changelog/`,
    detail: (id: QueryParam) => ({
      _root: `${apiUrl}/changelog/${id}/`,
      seen: `${apiUrl}/changelog/${id}/seen/`,
    }),
  },
  mindAcademy: {
    candidate: `${apiUrl}/mind-academy/candidate/`,
  },
  'external-job-offers': {
    automation: {
      _root: `${apiUrl}/external-job-offers/automation/`,
      sitemapOffers: `${apiUrl}/external-job-offers/automation/sitemap-offers/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/external-job-offers/automation/${id}/`,
        similarOffers: `${apiUrl}/external-job-offers/automation/${id}/similar-offers/`,
      }),
      searchSuggestions: `${apiUrl}/external-job-offers/automation/search-suggestions/`,
      companies: `${apiUrl}/external-job-offers/automation/companies/`,
    },
    company: {
      _root: `${apiUrl}/external-job-offers/company/`,
      detail: (id: QueryParam) =>
        `${apiUrl}/external-job-offers/company/${id}/`,
      searchSuggestions: `${apiUrl}/external-job-offers/company/search-suggestions/`,
    },
  },
  blog: {
    newsletterParticipation: `${apiUrl}/blog/newsletter-participation/`,
  },
  candidateCommunity: {
    posts: {
      _root: `${apiUrl}/candidate-community/posts/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/candidate-community/posts/${id}/`,
        like: `${apiUrl}/candidate-community/posts/${id}/like/`,
        unlike: `${apiUrl}/candidate-community/posts/${id}/unlike/`,
        comments: {
          _root: `${apiUrl}/candidate-community/posts/${id}/comments/`,
          detail: (commentId: QueryParam) => ({
            _root: `${apiUrl}/candidate-community/posts/${id}/comments/${commentId}/`,
            like: `${apiUrl}/candidate-community/posts/${id}/comments/${commentId}/like/`,
            unlike: `${apiUrl}/candidate-community/posts/${id}/comments/${commentId}/unlike/`,
          }),
        },
        report: `${apiUrl}/candidate-community/posts/${id}/report/`,
      }),
      tags: `${apiUrl}/candidate-community/posts/tags/`,
    },
    hotTopics: `${apiUrl}/candidate-community/hot-topics/`,
    profile: {
      me: `${apiUrl}/candidate-community/profile/me/`,
      detail: (id: QueryParam) => ({
        _root: `${apiUrl}/candidate-community/profile/${id}/`,
        follow: `${apiUrl}/candidate-community/profile/${id}/follow/`,
      }),
    },
  },
  bulletPoints: {
    points: {
      _root: `${apiUrl}/bullet-points/points/`,
      detail: (id: QueryParam) => `${apiUrl}/bullet-points/points/${id}/`,
    },
    fromText: {
      generateFromText: `${apiUrl}/bullet-points/from-text/generate-from-text/`,
      detail: (id: QueryParam) => `${apiUrl}/bullet-points/from-text/${id}/`,
    },
  },
  candidateProfile: {
    _root: `${apiUrl}/candidate-profile/`,
    me: `${apiUrl}/candidate-profile/me/`,
    fromFile: `${apiUrl}/candidate-profile/from-file/`,
    detail: (id: QueryParam) => ({
      _root: `${apiUrl}/candidate-profile/${id}/`,
    }),
  },
  geoSuggestion: {
    _root: `${apiUrl}/geo-suggestion/`,
  },
  search: {
    reasoningSearch: {
      _root: `${apiUrl}/search/reasoning-search/`,
      detail: (id: QueryParam) => `${apiUrl}/search/reasoning-search/${id}/`,
      candidates: {
        detail: (id: QueryParam) =>
          `${apiUrl}/search/reasoning-search/candidates/${id}/`,
      },
    },
  },
  offerAdmin: {
    _root: `${apiUrl}/offer-admin/`,
    detail: (id: QueryParam) => `${apiUrl}/offer-admin/${id}/`,
    recruiteeJobs: `${apiUrl}/offer-admin/recruitee-jobs/`,
  },
  wordpress: {
    pages: {
      _root: `${wordpressApiUrl}/pages/`,
    },
  },
  competencyAssessment: {
    sessions: {
      _root: `${apiUrl}/competency/sessions/`,
      session: (id: QueryParam) => ({
        _root: `${apiUrl}/competency/sessions/${id}/`,
        messages: `${apiUrl}/competency/sessions/${id}/messages/`,
      }),
    },
  },
  resumeBuilder: {
    _root: `${apiUrl}/resume-builder/`,
    fromFile: `${apiUrl}/resume-builder/from-file/`,
    detail: (id: QueryParam) => ({
      _root: `${apiUrl}/resume-builder/${id}/`,
      score: `${apiUrl}/resume-builder/${id}/score/`,
      aiIssueFix: `${apiUrl}/resume-builder/${id}/ai-issue-fix/`,
      generateDescription: `${apiUrl}/resume-builder/${id}/generate_description/`,
      generateSkills: `${apiUrl}/resume-builder/${id}/generate_skills/`,
      generateSuggestions: `${apiUrl}/resume-builder/${id}/generate_suggestions/`,
      generateSummary: `${apiUrl}/resume-builder/${id}/generate_summary/`,
    }),
  },
};

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;
